import { API_URL, tenantConfig } from "config";
import AuthService from "services/AuthService";
import { HttpError, HttpService } from "services/HttpService";
import { Completions } from "store/types/Completions";
import { CredentialSearchResults } from "store/types/CredentialSearchResults";
import { Credentials } from "store/types/Credentials";

interface ReportQueueResponse {
  ReportId: number;
}

class CredentialService {
  public static getMyCredentials(nccerNumber: string): Promise<Credentials> {
    return new Promise<Credentials>((resolve: (data: Credentials) => void, reject: (error: HttpError) => void) => {
      HttpService.get(`${API_URL}/credentials?nccerNumber=${nccerNumber}`, {
        headers: {
          TenantId: tenantConfig.id,
          authorization: AuthService.getBearerToken(),
        },
      })
        .toPromise()
        .then((data: Credentials) => {
          resolve(data);
        })
        .catch((error: HttpError) => {
          reject(error);
        });
    });
  }

  public static getCompletions(nccerNumber: string, partials = "true", oq = true): Promise<Completions> {
    return new Promise<Completions>((resolve: (data: Completions) => void, reject: (error: HttpError) => void) => {
      HttpService.get(`${API_URL}/credentials/completions?nccerNumber=${nccerNumber}&partials=${partials}&oq=${oq}`, {
        headers: {
          TenantId: tenantConfig.id,
          authorization: AuthService.getBearerToken(),
        },
      })
        .toPromise()
        .then((data: Completions) => {
          resolve(data);
        })
        .catch((error: HttpError) => {
          reject(error);
        });
    });
  }

  public static search(nccerNumbers: string, programId: string): Promise<CredentialSearchResults> {
    return new Promise<CredentialSearchResults>((resolve: (data: CredentialSearchResults) => void, reject: (error: HttpError) => void) => {
      HttpService.get(
        `${API_URL}/credentials/search?nccerNumbers=${nccerNumbers}&programId=${programId}&partials=true`,
        {
          headers: {
            TenantId: tenantConfig.id,
            authorization: AuthService.getBearerToken(),
          },
        }
      )
        .toPromise()
        .then((data: CredentialSearchResults) => {
          resolve(data);
        })
        .catch((error: HttpError) => {
          reject(error);
        });
    });
  }

  public static publicSearch(nccerNumber: string): Promise<Completions> {
    return new Promise<Completions>((resolve: (data: Completions) => void, reject: (error: HttpError) => void) => {
      HttpService.get(`${API_URL}/credentials/publicSearch?nccerNumber=${nccerNumber}`, {
        headers: {
          TenantId: tenantConfig.id,
        },
      })
        .toPromise()
        .then((data: Completions) => {
          resolve(data);
        })
        .catch((error: HttpError) => {
          reject(error);
        });
    });
  }

  public static claimWalletCard(nccerNumber: string): Promise<boolean> {
    return new Promise<boolean>((resolve: (data: boolean) => void, reject: (error: HttpError) => void) => {
      HttpService.post(`${API_URL}/credentials/claimWalletCard`, {
        headers: {
          TenantId: tenantConfig.id,
          authorization: AuthService.getBearerToken(),
        },
      })
        .toPromise()
        .then((data: boolean) => {
          resolve(data);
        })
        .catch((error: HttpError) => {
          reject(error);
        });
    });
  }

  public static getTranscriptLink(nccerNumber: string): Promise<string> {
    return new Promise<string>((resolve: (data: string) => void, reject: (error: HttpError) => void) => {
      HttpService.get(`${API_URL}/credentials/transcript?nccerNumber=${nccerNumber}`, {
        headers: {
          TenantId: tenantConfig.id,
          authorization: AuthService.getBearerToken(),
        },
      })
        .toPromise()
        .then((data: string) => {
          resolve(data);
        })
        .catch((error: HttpError) => {
          reject(error);
        });
    });
  }

  public static getTranscriptPipelineLink(nccerNumber: string): Promise<string> {
    return new Promise<string>((resolve: (data: string) => void, reject: (error: HttpError) => void) => {
      HttpService.get(`${API_URL}/credentials/transcript-pipeline?nccerNumber=${nccerNumber}`, {
        headers: {
          TenantId: tenantConfig.id,
          authorization: AuthService.getBearerToken(),
        },
      })
        .toPromise()
        .then((data: string) => {
          resolve(data);
        })
        .catch((error: HttpError) => {
          reject(error);
        });
    });
  }

  public static getDocumentLink(referenceLink: string): Promise<string> {
    return new Promise<string>((resolve: (data: string) => void, reject: (error: HttpError) => void) => {
      HttpService.get(`${API_URL}/credentials/documentLink?referenceLink=${encodeURIComponent(referenceLink)}`, {
        headers: {
          TenantId: tenantConfig.id,
          authorization: AuthService.getBearerToken(),
        },
      })
        .toPromise()
        .then((data: string) => {
          resolve(data);
        })
        .catch((error: HttpError) => {
          reject(error);
        });
    });
  }
}

export default CredentialService;
